import {
  Box,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { PlayArrow } from "@mui/icons-material";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";

const defaultOptions = [
  "Mini Teatro de Lectura",
  "Desafío Rápido y Correcto",
  "Lectura en Eco + Lectura en Coro",
  "Caza de errores",
  "La Búsqueda Lectora",
  "Radio familiar"
];

const colors = [
  "#A8E6CF",
  "#B3D7E8",
  "#F7B7A3",
  "#F9E79F",
  "#D2B4DE",
  "#A3C4F3",
  "#FFB3BA",
  "#C2D5F1",
  "#D5DBDB",
];

export default function RuletaJuegos() {
  const [options] = useState<string[]>(defaultOptions);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState<string | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);
  const { setPromptData, setEnvoltorio } = useActividadesContext();

  const segmentAngle = 360 / options.length;

  const optionToWrapperMap: Record<string, string> = {
    "Mini Teatro de Lectura": "mini_teatro_lectura",
    "Desafío Rápido y Correcto": "desafio_rapido",
    "Lectura en Eco + Lectura en Coro": "lectura_eco_y_coro",
    "Caza de errores": "caza_errores",
    "La Búsqueda Lectora": "busqueda_lectores",
    "Radio familiar": "radio_familiar",
  };

  const spinWheel = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setWinner(null);

    const duration = 2000 + Math.random() * 3000; // NOSONAR: visual randomness for game animation, not security-sensitive
    const finalAngle = 360 * 5 + Math.random() * 360; // NOSONAR: visual randomness for game animation, not security-sensitive
    const newRotation = rotation + finalAngle;

    setRotation(newRotation);

    setTimeout(() => {
      const normalizedAngle = (360 - (newRotation % 360)) % 360;
      const winnerIndex = Math.floor(normalizedAngle / segmentAngle);
      const selectedWinner = options[winnerIndex];

      setWinner(selectedWinner);

      const envoltorio = optionToWrapperMap[selectedWinner] || "ruleta_juegos";

      setPromptData((prev: any) => ({
        ...prev,
        ganador: selectedWinner,
        envoltorio, 
      }));
      setEnvoltorio(envoltorio);

      setIsSpinning(false);
    }, duration);
  };


  const splitText = (text: string, maxLength = 10) => {
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      if ((currentLine + word).length <= maxLength) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);

    return lines.slice(0, 2);
  };

  return (
    <Grid container spacing={2} alignItems="center" justifyContent="center" flexWrap="wrap" mt={5}>
      {/*Título y texto */}
      <Grid item xs={12} md={6}>
        <Box textAlign={{ xs: "center" }}>
          <Typography variant="h4" fontWeight="bold" color="#336B87" fontSize={54} gutterBottom>
            Ruleta de juegos
          </Typography>
          <Typography color="text.secondary" mb={2}>
            <strong> ¡Gira la ruleta y descubre tu próxima actividad! </strong>
          </Typography>
          <Typography color="text.secondary" mb={4}>
            Presiona <strong>Jugar</strong> para girar la ruleta. Cada giro tendrá una duración diferente y el resultado será completamente aleatorio.
            <br /><br />
            ¡Diviértete explorando diferentes actividades y descubre nuevas formas de disfrutar aprendiendo!
          </Typography>

          <Box textAlign="center" mb={4}>
            {winner && !isSpinning && (
              <Box bgcolor="#278C62" px={4} py={2} borderRadius={3} boxShadow={3} textAlign="center">
                <Typography variant="h6" fontWeight="bold" color="white">
                  ¡Resultado!
                </Typography>
                <Typography color="white">{winner}</Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" gap={2}>
            <Button
              onClick={spinWheel}
              disabled={isSpinning}
              variant="contained"
              startIcon={<PlayArrow />}
              sx={{ bgcolor: "#336B87", '&:hover': { bgcolor: "#2A5A75" }, borderRadius: 16, m:"auto" }}
            >
              {isSpinning ? "Girando..." : "Jugar"}
            </Button>

            {/* Botón para editar opciones<Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setDialogOpen(true)}
            sx={{ borderColor: "#336B87", color: "#336B87", '&:hover': { bgcolor: "#336B87", color: "white" } }}
          >
            Editar Opciones
          </Button>*/}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6} display="flex" justifyContent="center">
        <Box position="relative">
          <Box
            sx={{
              width: 24,
              height: 32,
              bgcolor: "#336B87",
              borderBottomLeftRadius: 999,
              borderBottomRightRadius: 999,
              position: "absolute",
              top: -20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              boxShadow: 3,
            }}
          >
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: "3px solid transparent",
                borderRight: "3px solid transparent",
                borderTop: "6px solid #336B87",
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            />
          </Box>

          <Box
            ref={wheelRef}
            sx={{
              width: 384,
              height: 384,
              borderRadius: "50%",
              border: "4px solid #336B87",
              boxShadow: 3,
              transform: `rotate(${rotation}deg)`,
              transition: isSpinning ? "transform 3s ease-out" : undefined,
            }}
          >
            <svg viewBox="0 0 200 200" width="100%" height="100%">
              {options.map((option, index) => {
                const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
                const x1 = 100 + 90 * Math.cos(startAngle);
                const y1 = 100 + 90 * Math.sin(startAngle);
                const x2 = 100 + 90 * Math.cos(endAngle);
                const y2 = 100 + 90 * Math.sin(endAngle);
                const pathData = `M 100 100 L ${x1} ${y1} A 90 90 0 ${segmentAngle > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
                const textAngle = (startAngle + endAngle) / 2;
                const textX = 100 + 55 * Math.cos(textAngle);
                const textY = 100 + 55 * Math.sin(textAngle);
                const textRotationAngle = (textAngle * 180) / Math.PI + 90;
                const lines = splitText(option);

                return (
                  <g key={index}>
                    <path d={pathData} fill={colors[index % colors.length]} stroke="#fff" strokeWidth={1.5} />
                    {lines.map((line, lineIndex) => (
                      <text
                        key={lineIndex}
                        x={textX}
                        y={textY + (lineIndex - (lines.length - 1) / 2) * 8}
                        fill="#000"
                        fontSize="6"
                        fontWeight="500"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${textRotationAngle}, ${textX}, ${textY})`}
                        style={{
                          filter: "drop-shadow(0.5px 0.5px 0.5px rgba(255,255,255,0.8))",
                          textShadow: "0.5px 0.5px 1px rgba(255,255,255,0.8)",
                        }}
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                );
              })}
            </svg>
          </Box>
        </Box>



        {/** CRUD opciones 
        
       <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Editar Opciones
            <IconButton
              aria-label="close"
              onClick={() => setDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2}>
              {options.map((option, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1}>
                  <Box width={16} height={16} borderRadius="50%" flexShrink={0} sx={{ bgcolor: colors[index % colors.length] }} />
                  <TextField
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    fullWidth
                    size="small"
                  />
                  {options.length > 2 && (
                    <IconButton color="error" size="small" onClick={() => removeOption(index)}>
                      <Close />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  placeholder="Nueva opción..."
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addOption()}
                  size="small"
                />
                <IconButton onClick={addOption} sx={{ bgcolor: "#278C62", color: "white", '&:hover': { bgcolor: "#1F6B4A" } }}>
                  <Add fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Tip: los textos largos se dividirán automáticamente en dos líneas para mejor legibilidad.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} autoFocus>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>*/}
      </Grid>
    </Grid>
  );
}